// BookStatAndAction.js
import { AppDispatch, RootState } from "@/redux/store";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TbEyeglass } from "react-icons/tb";
import { Rate, Skeleton, Spin } from "antd";
import { definitions } from "../../../services/metruyenvl";
import { useAuth } from "@/hooks";
import Image from "next/image";
import { displayNumber, formatValueReadings } from "@/utils/formatted";
import { TiTick } from "react-icons/ti";
import { BsBookmark } from "react-icons/bs";
import { useRouter } from "next/navigation";
import ReactionBooks from "./reaction-book";
import { suggestAPI } from "@/services/suggestion_api";
import alertUtils from "@/utils/alert";
import { showModalForm } from "@/redux/reducers/user.reducer";
import bookmarkAPI from "@/services/bookmark_api";
import { statAPI } from "@/services/stat_api";
import { ResponseTypes } from "@/types/constants";
import { userActionAPI } from "@/services/user_action_api";
import { BookData } from "@/redux/reducers/book.reducer";

const BookStatAndAction = ({ slug }: { slug: string }) => {
    const dispatch = useDispatch<AppDispatch>();
    const router = useRouter();
    const { isAuth } = useAuth();

    const bookData: BookData = useSelector((state: RootState) => state.bookReducer.map[slug as string]);

    const [userAction, setUserAction] = useState<definitions["do.UserActionDO"]>();
    const [bookmarkLoading, setBookmarkLoading] = useState<boolean>(false);

    const [bookStats, setBookStats] = useState<definitions["do.StatsDO"]>();

    const getUserAction = async () => {
        if (isAuth) {
            try {
                const res: ResponseTypes = await userActionAPI.getUserAction("book", bookData?.detail?.id as string);
                setUserAction(res.data);
            } catch (error) {
                console.log(error);
            }
        }
    };

    useEffect(() => {
        setBookStats(bookData?.detail?.stats);
    }, [dispatch, bookData?.detail?.stats]);

    useEffect(() => {
        if (isAuth && !!bookData?.detail?.id) {
            getUserAction();
        }
        console.log("BookStatAndAction -> call useEffect getUserAction", bookData);
    }, [dispatch, bookData, bookData?.userAction]);

    const onClickDeleteBookmark = async () => {
        const deleteBookmarked = async () => {
            setBookmarkLoading(true);

            try {
                const res: ResponseTypes = await bookmarkAPI.delete(bookData.detail.id as string);
                setBookStats(res.data);

                if (!!userAction) {
                    userAction.bookmarked = false;
                    setUserAction(userAction);
                }
            } catch (error) {
                console.log(error);
            }

            setBookmarkLoading(false);
        };

        if (isAuth) {
            deleteBookmarked();
        } else {
            dispatch(showModalForm(true));
        }
    };

    const onClickCreateBookmark = async () => {
        const createBookmark = async () => {
            setBookmarkLoading(true);

            try {
                const res: ResponseTypes = await bookmarkAPI.create({ bookID: bookData.detail.id as string });
                setBookStats(res.data);

                if (!!userAction) {
                    userAction.bookmarked = true;
                    setUserAction(userAction);
                }
            } catch (error) {
                console.log(error);
            }

            setBookmarkLoading(false);
        };

        if (isAuth) {
            createBookmark();
        } else {
            dispatch(showModalForm(true));
        }
    };

    const postSuggestion = async () => {
        const createSuggestion = async (point: number) => {
            try {
                const res: ResponseTypes = await suggestAPI.postSuggestion({ book_id: bookData.detail.id as string, point: point });
                setBookStats(res.data);

                if (!!userAction) {
                    let userPoint = userAction.suggestion_point ?? 0;
                    userAction.suggestion_point = userPoint + point;
                    setUserAction(userAction);
                }
            } catch (error) {
                console.log(error);
            }
        };

        if (isAuth) {
            try {
                const res = await createSuggestion(1);
                alertUtils.toastSuccess("Đề cử thành công");

                // getBookStats();
            } catch (error) {
                alertUtils.toastError("Bạn không đủ coin để đề cử");
            }
        } else {
            dispatch(showModalForm(true));
        }
    };

    return (
        <div>
            <div className="flex items-center mt-5 gap-x-12 md:justify-start justify-center">
                <div>
                    <div className="text-base font-semibold">{bookStats ? bookStats?.chapters_count : <Skeleton.Avatar active shape="square" size="small" />}</div>
                    <div className="text-xs mt-2">Chương</div>{" "}
                </div>
                <div>
                    <div className="text-base font-semibold">{bookStats ? formatValueReadings(!!bookStats?.reacts_count && bookStats?.reacts_count > 0 ? bookStats?.reacts_count : 0) : <Skeleton.Avatar active shape="square" size="small" />}</div>
                    <div className="text-xs mt-2">Lượt đọc </div>{" "}
                </div>
                <div>
                    <div className="text-base font-semibold">{bookStats ? formatValueReadings(!!bookStats?.bookmarks_count && bookStats?.bookmarks_count > 0 ? bookStats?.bookmarks_count : 0) : <Skeleton.Avatar active shape="square" size="small" />}</div>
                    <div className="text-xs mt-2">Đánh dấu </div>{" "}
                </div>
                <div>
                    <div className="text-base font-semibold">{bookStats ? formatValueReadings(!!bookStats?.comments_count && bookStats?.comments_count > 0 ? bookStats?.comments_count : 0) : <Skeleton.Avatar active shape="square" size="small" />}</div>
                    <div className="text-xs mt-2">Bình luận </div>{" "}
                </div>
            </div>
            <div className="flex items-center mt-5 gap-x-3 md:justify-start justify-center">
                {!!bookStats ? (
                    <Rate value={bookStats?.reviews_point ?? 5} disabled style={{ columnGap: "2px" }} />
                ) : (
                    <>
                        <Skeleton.Avatar active shape="circle" size="small" />
                        <Skeleton.Avatar active shape="circle" size="small" />
                        <Skeleton.Avatar active shape="circle" size="small" />
                        <Skeleton.Avatar active shape="circle" size="small" />
                    </>
                )}

                <div className="text-sm mt-1">
                    {!!bookStats ? displayNumber(bookStats?.reviews_point ?? 5) : <Skeleton.Avatar active shape="square" size="small" />}/5.0 {<span>({!!bookStats ? bookStats?.reviews_count || 0 : <Skeleton.Avatar active shape="square" size="small" />} đánh giá)</span>}
                </div>
            </div>
            <div className="flex md:flex-row flex-col md:items-center gap-y-2 lg:gap-x-10 md:gap-x-2 lg:mt-5 mt-2 font-semibold relative">
                {" "}
                <ReactionBooks book={bookData?.detail} userAction={userAction as definitions["do.UserActionDO"]} />
                <button
                    onClick={() => {
                        const chapterSlug = userAction?.reading_chapter?.slug || bookData?.detail.first_chapter?.slug;
                        router.push(`/truyen/${bookData?.detail.slug}/${chapterSlug}`);
                    }}
                    disabled={!bookStats?.chapters_count}
                    className="lg:p-3 p-2 pr-4 pl-4 bg-orange-800 rounded-3xl lg:text-base text-sm flex items-center justify-center md:justify-start gap-x-1"
                >
                    <TbEyeglass color="white" size={20} />
                    <div className="text-white"> Đọc {userAction?.reading_chapter ? "tiếp" : "truyện"}</div>
                </button>{" "}
                <Spin spinning={bookmarkLoading}>
                    {userAction?.bookmarked ? (
                        <button onClick={onClickDeleteBookmark} className="lg:p-3 p-2 pr-4 pl-4 border border-solid justify-center md:justify-start w-full md:w-auto border-slate-400 rounded-3xl flex items-center gap-x-1">
                            <TiTick size={20} color="#64748b" />
                            <div className="text-slate-500 font-semibold lg:text-base text-sm"> Đã lưu </div>
                        </button>
                    ) : (
                        <button onClick={onClickCreateBookmark} className="lg:p-3 p-2 pr-4 pl-4 border border-solid justify-center md:justify-start w-full md:w-auto border-slate-400 rounded-3xl flex items-center gap-x-1">
                            <BsBookmark size={20} color="#64748b" />
                            <div className="text-slate-500 lg:text-base text-sm font-semibold"> Đánh dấu </div>
                        </button>
                    )}
                </Spin>
                {bookData?.detail?.status && (
                    <button className="lg:p-3 p-2 pr-4 pl-4 border border-solid justify-center cursor-auto md:justify-start border-green-400 rounded-3xl flex items-center gap-x-1">
                        <Image src="/complete.png" alt="" width="6" height="6" className="h-6 w-6" />
                        <div className="text-green-500 lg:text-base text-sm"> Đã hoàn thành</div>
                    </button>
                )}
                <button
                    onClick={() => {
                        postSuggestion();
                    }}
                    className="flex lg:p-3 p-2 pr-4 pl-4 bg-amber-100 border border-solid justify-center md:justify-start border-amber-400 rounded-3xl items-center gap-x-1"
                >
                    <Image src="/trophy.png" alt="" width="6" height="6" className="h-6 w-6" />
                    <div className="text-amber-600 lg:text-base text-sm"> Đề cử</div>
                </button>
            </div>
            <style jsx>{`
                .list_emotion {
                    transition: 0.3s ease-in-out;
                }
            `}</style>
        </div>
    );
};

export default BookStatAndAction;
